/**
 * To get price updates in mocked mode - do:
 *   (1) In `configDebug.ts` - set MOCK.TRADING_MODE to `up`, `down` or `random` to get dynamic price updates.
 *   (2) In `configDebug.ts` - also check the TRADING_MDS_REFRESH_RATE and set it to your needs.
 *   (3) In `libSettings.ts` - check the THROTTLE_TIME_PRICE_UPDATES_MS and set it to your needs.
 *       It will affect TRADING_MDS_REFRESH_RATE by throttling these updates.
 *
 * NOTE: If the above is not done correctly - price updates will not be visible.
 */
import { produce } from 'immer';
import { entity } from 'simpler-state';

import { logConfig, MOCK } from '../../configDebug';
import entityReduxLogger from '../debug/helpers/entity-redux-logger';
import { MarketCountryCode, StockExchangeCodeType } from '../models/fundamental/types';
import { GWMarketDataSummaryBucket } from '../models/gateway/types';
import { OHLCV, TradeData } from '../models/market-data/types';
import { SymbolPriceData } from '../store/market-data/charting/types';
import {
  EMPTY_CALCULATED_PRICE_DATA,
  EMPTY_PRICE_DATA,
  parseSymbolPriceSourceData,
} from '../store/market-data/priceSource.helpers';
import { InstrumentSnapshotData, InstrumentSnapshotMap, MultipleOHLCVDataForPriceCalculation, TradeDataCache } from '../store/market-data/types';
import { CalculatedSymbolPrices, calculateSymbolPrices } from '../util/ChartingHelpers';
import { random } from '../util/collectionHelpers';
import { formatNumber, formatNumberWithSuffix } from '../util/DataHelpers';
import { isObject } from '../util/typeGuards';
import { NullableNumber, NullableString, ZERO_PERCENT, ZERO_VALUE } from '../util/types';

import { updateBigChartLineClose } from './BigChartCache';
import { PriceChangeDirection } from './priceTypes';

const isDevEnv = process.env.NODE_ENV !== 'production';

export type CalculatedTradePrice = {
  symbol: string
  prevPrice: NullableNumber
  currentPrice: NullableNumber
  currentPriceDate?: string
  change: string
  changePercent: string
  direction: PriceChangeDirection
  priceData: SymbolPriceData
  calculatedSymbolPrices: CalculatedSymbolPrices
  changePercentWithSign: string
  hasTradeUpdate?: boolean
  hasInstrumentSnapshotData?: boolean
  has1DChartData?: boolean
  has3MChartData?: boolean
  tradeTime?: string
  hasReceivedDataResponse?: boolean
}

export type CalculatedTradePriceDataMap = Partial<Record<string, CalculatedTradePrice>>


const generateEmptyTradePrice = (symbol: NullableString): CalculatedTradePrice => ({
  symbol: symbol ?? '',
  currentPrice: null,
  prevPrice: null,
  change: ZERO_VALUE,
  changePercent: ZERO_PERCENT,
  direction: '',
  priceData: EMPTY_PRICE_DATA,
  calculatedSymbolPrices: EMPTY_CALCULATED_PRICE_DATA,
  changePercentWithSign: '',
  tradeTime: '',
});

type TemporaryDataMap = { [symbol: string]: TradeData | InstrumentSnapshotData | OHLCV[] }

function generateDataIfMockedMode(state: CalculatedTradePriceDataMap, newDataMap: TemporaryDataMap) {
  if (MOCK.ENABLED && MOCK.TRADING_MODE === 'static') {
    const priceData = {
      lastPrice: random(26.84, 33.52, true),
      close: random(23.12, 31.89, true),
      afterClose: random(24.67, 30.73, true),
      previousClose: random(20.00, 70.00, true),
      previousAfterClose: random(24.67, 30.73, true),
      prevButOneClose: random(20.00, 70.00, true),
      hasDataForToday: true,
      hasAfterMarketData: true,
    };
    for (const symbol in newDataMap) {
      const tradeItem: CalculatedTradePrice = state[symbol] ?? generateEmptyTradePrice(symbol);
      updateItemWithPriceData(tradeItem, priceData, MarketCountryCode.UnitedStates, symbol);
    }
    return true;
  }

  return false;
}

/**
 * Every field written by `updateItemWithPriceData` is derived from these values, so when they all
 * match the cached item the update is a no-op. Bailing out early skips three `numbro` formats per
 * symbol and - because nothing is written to the immer draft - keeps the cached item's identity, so
 * subscribed rows don't re-render.
 */
const CALCULATED_PRICE_KEYS = [
  'lastTradePrice',
  'lastTradeTime',
  'currentMainPrice',
  'prevMainPrice',
  'currentExtraPrice',
  'prevExtraPrice',
  'mainPriceDirection',
  'changeMainPrice',
  'changedPercentMainPrice',
  'extraPriceDirection',
  'changeExtraPrice',
  'changedPercentExtraPrice',
  'hasDataForToday',
  'hasAfterMarketData',
] as const;

function isSameCalculatedPrices(prev: CalculatedSymbolPrices, next: CalculatedSymbolPrices): boolean {
  const prevValues = prev as Record<string, unknown>;
  const nextValues = next as Record<string, unknown>;

  for (let index = 0; index < CALCULATED_PRICE_KEYS.length; index += 1) {
    const key = CALCULATED_PRICE_KEYS[index];
    if (prevValues[key] !== nextValues[key]) return false;
  }

  return true;
}

function updateItemWithPriceData(
  itemToUpdate: CalculatedTradePrice,
  data: SymbolPriceData | TradeData,
  country: MarketCountryCode,
  symbol?: string,
) {

  const isSymbolPriceData = (
    isObject(data)
    && (
      'lastPrice' in data
      || 'afterClose' in data
      || 'hasDataForToday' in data
      || 'hasAfterMarketData' in data
    )
  );
  let priceData: SymbolPriceData = EMPTY_PRICE_DATA;
  let tradeData: TradeData | null = null;

  if (isSymbolPriceData) {
    priceData = data as SymbolPriceData;
  } else {
    priceData = itemToUpdate.priceData;
    tradeData = data as TradeData;
  }

  // don't update prices unless it is a trade price update after it has been received (see if at the end of this function)
  if (itemToUpdate.hasTradeUpdate && !tradeData) return;

  const calculatedSymbolPrices = calculateSymbolPrices(priceData, country, tradeData);
  const tradeTime = (data as TradeData).tradetime;
  const {
    prevMainPrice, currentMainPrice, changeMainPrice, changedPercentMainPrice,
    mainPriceDirection,
  } = calculatedSymbolPrices;

  // lock updating price once trade price has been received and prev price exists
  if (!itemToUpdate.hasTradeUpdate && tradeData && prevMainPrice != null) {
    itemToUpdate.hasTradeUpdate = true;
  }

  // Nothing observable changed - skip the formatting and leave the cached item untouched (see above).
  if (
    itemToUpdate.priceData === priceData
    && itemToUpdate.tradeTime === tradeTime
    && isSameCalculatedPrices(itemToUpdate.calculatedSymbolPrices, calculatedSymbolPrices)
  ) {
    return;
  }

  const changePercent = formatNumberWithSuffix(changedPercentMainPrice, '%');

  itemToUpdate.direction = mainPriceDirection;
  itemToUpdate.prevPrice = prevMainPrice;
  itemToUpdate.currentPrice = currentMainPrice;
  itemToUpdate.change = formatNumber(changeMainPrice);
  itemToUpdate.changePercent = changePercent;
  itemToUpdate.priceData = priceData;
  itemToUpdate.calculatedSymbolPrices = calculatedSymbolPrices;
  itemToUpdate.changePercentWithSign = `${mainPriceDirection}${changePercent}`;
  itemToUpdate.tradeTime = tradeTime;

}

function updateNewState(
  newData: TradeDataCache | InstrumentSnapshotMap | MultipleOHLCVDataForPriceCalculation,
  state: CalculatedTradePriceDataMap,
  source: 'instr' | 'trade' | 'chart-data',
  exchangeCode?: StockExchangeCodeType,
) {
  const countryCode = MarketCountryCode.UnitedStates;
  const newDataMap = (
    source === 'chart-data'
      ? (newData as MultipleOHLCVDataForPriceCalculation).data
      : newData as TradeDataCache | InstrumentSnapshotMap
  ) as TemporaryDataMap;

  // MOCK: Generate mocked data if enabled
  if (generateDataIfMockedMode(state, newDataMap)) return;

  const { priceFlow } = logConfig;

  if (priceFlow) {
    const debugData: any = newData?.data ?? newData;
    const debugSymbols = Object.keys(debugData);
    const debugState = {} as any;
    const debugInstrItems = source === 'instr' ? debugSymbols.join() : '';
    const debugChartBucket = newData?.bucket ? `-${newData.bucket}` : '';
    const debugChartItems = source === 'chart-data' ? Object.keys(newData.data).join() : '';
    debugSymbols.forEach(symbol => { debugState[symbol] = debugData[symbol]; });
    console.log(`Trade-update -- ${source}${debugChartBucket} ${debugChartItems} ${debugInstrItems} [price-flow]`);  
    console.debug(`Trade-update -- ${source}${debugChartBucket} ${debugChartItems} ${debugInstrItems} [price-flow]`, {newData, state});  
  }

  Object.entries(newDataMap).forEach(([ symbol, data ]) => {
    const tradeItem: CalculatedTradePrice = state[symbol] ?? generateEmptyTradePrice(symbol);

    if (!state[symbol]) {
      state[symbol] = tradeItem;
    }

    // Writing an unchanged flag would still mark the immer draft dirty and re-render every
    // subscriber, so only write it the first time.
    if (!tradeItem.hasReceivedDataResponse) {
      tradeItem.hasReceivedDataResponse = true;
    }

    switch (source) {
      case 'instr': {
        const instrumentSnapshot = data as InstrumentSnapshotData;
        const priceData = parseSymbolPriceSourceData('instrument-snapshot', instrumentSnapshot, tradeItem.priceData, countryCode);
        if (!tradeItem.hasInstrumentSnapshotData) {
          tradeItem.hasInstrumentSnapshotData = true;
        }
        updateItemWithPriceData(tradeItem, priceData, countryCode, symbol);
        if (Object.keys(newDataMap).length === 1) {
          updateBigChartLineClose(priceData.lineClose);
        }
        break;
      }

      case 'trade': {
        const trade = data as TradeData;
        updateItemWithPriceData(tradeItem, trade, countryCode, symbol);
        break;
      }

      case 'chart-data': {
        const { bucket } = newData as MultipleOHLCVDataForPriceCalculation;
        const dataFromCache = newDataMap as { [symbol: string]: OHLCV[] };
        const ohlcvData = dataFromCache[symbol]; // check if no data available
        if (!ohlcvData) break;

        switch (bucket) {
          case GWMarketDataSummaryBucket.OneDay:
            tradeItem.has1DChartData = true;
            break;
          case GWMarketDataSummaryBucket.ThreeMonths:
            tradeItem.has3MChartData = true;
            break;
          default:
            break;
        }

        const priceData = parseSymbolPriceSourceData('chart-data', { bucket, data: ohlcvData }, tradeItem.priceData, countryCode);
        updateItemWithPriceData(tradeItem, priceData, countryCode, symbol);
        break;
      }

      default:
        break;
    }
  });

  if (isDevEnv && logConfig.tradePriceCacheUpdates) {
    const { tradePriceCacheUpdatesSymbol: rawSymbol } = logConfig;
    const filterSymbol = rawSymbol ? `${rawSymbol}`.toUpperCase() : null;
    let debugState: CalculatedTradePriceDataMap = { ...(
      filterSymbol
        ? { [filterSymbol]: state[filterSymbol] }
        : state
    ) };
    let titles = '\n';
    const debugData: any = {};
    Object.entries(debugState).forEach(([ debugSymbol, debugSymbolState ]) => {
      const { prevPrice, currentPrice, change, changePercent } = (debugSymbolState ?? {}) as CalculatedTradePrice;
      titles += `\t\t${debugSymbol}  => ${prevPrice}->${currentPrice}  ${change} (${changePercent})\n`;
      debugData[debugSymbol] = { debugSymbolState, newData };
    });
    const src = source === 'chart-data' ? `${source}/${(newData as MultipleOHLCVDataForPriceCalculation).bucket}` : source;
    const msg = `[TradePriceCache] State updated (${src}) for ${titles}`;
    console.log(msg);
    console.debug(msg, debugData);
  }
}

const defaultTransform = (symbol: NullableString) => (
  (data: CalculatedTradePriceDataMap) => (symbol ? data[symbol] : undefined)
);
type TransformFunction = (
  (data: CalculatedTradePriceDataMap) => CalculatedTradePrice | NullableNumber | NullableString
)

const TradePriceCache = entity({} as CalculatedTradePriceDataMap, entityReduxLogger('TradePrice', 'trade'));

/** Used only by Gateway service to update cache from response messages. */
export const updateTradePriceCacheFromInstrumentSnapshot = (
  newItems: InstrumentSnapshotMap,
  exchangeCode?: StockExchangeCodeType,
) => {
  TradePriceCache.set(
    produce(state => updateNewState(newItems, state, 'instr', exchangeCode)),
  );
};

/** Used only by Gateway service to update cache from response messages. */
export const updateTradePriceCacheFromTrade = (newItemsRaw: TradeDataCache, exchangeCode?: StockExchangeCodeType) => {
  if (!newItemsRaw) return;

  const count = Object.keys(newItemsRaw).length;
  if (count) {
    TradePriceCache.set(produce(state => updateNewState(newItemsRaw, state, 'trade', exchangeCode)));
  }
};

/** Used only by Gateway service to update cache from response messages. */
export const updateTradePriceCacheFromChartData = (
  newData: MultipleOHLCVDataForPriceCalculation,
  exchangeCode?: StockExchangeCodeType,
) => {
  TradePriceCache.set(
    produce(state => updateNewState(newData, state, 'chart-data', exchangeCode)),
  );
};

// exporting setter only for unit testing and mocked mode
export const _setTradePricesCache_InUnitTestsAndMockedMode = TradePriceCache.set;  

// hiding direct setter by not including it in exports
export default {
  /**
   * @param caller Used when debugging to differentiate between cases. The default one extracts the particular symbol changes only.
   */
  use: (symbol: NullableString, caller: string) => (
    (TradePriceCache.use(defaultTransform(symbol)) ?? generateEmptyTradePrice(symbol))
  ),
  /**
   * Be careful when using `useCustom` - read simpler-state documentation to use the right transform function
   */
  useCustom: TradePriceCache.use,
  get: (symbol: NullableString) => (
    (symbol ? TradePriceCache.get()[symbol] : undefined) ?? generateEmptyTradePrice(symbol)
  ),
  getAll: TradePriceCache.get,
};
